import { useState } from "react";
import { RotateCcw, Save, X } from "lucide-react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { PictogramImage } from "./PictogramImage";

const BASE_COLOR = "#1A1A1A";

export const DefaultPictogramEditor = ({ picto, categories, reload, onClose, onReturnFocus }) => {
  const [name, setName] = useState(picto.name);
  const [color, setColor] = useState(picto.color_hex || BASE_COLOR);
  const [categoryId, setCategoryId] = useState(picto.category_id);
  const [useOriginal, setUseOriginal] = useState(Boolean(picto.preserve_original && !picto.color_hex));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const effectiveColor = useOriginal ? null : color;
  const changed = name !== picto.name || categoryId !== picto.category_id ||
    (effectiveColor || "").toUpperCase() !== (picto.color_hex || "").toUpperCase();
  const options = categories.filter(c => c.is_default && c.is_system);
  const current = categories.find(c => c.id === categoryId);

  const save = async (reset = false) => {
    setBusy(true); setError("");
    try {
      await api.put(`/pictograms/${picto.id}/customization`, reset
        ? { name: null, color_hex: null, category_id: null }
        : { name: name.trim() || null, color_hex: effectiveColor, category_id: categoryId });
      await reload();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Impossibile salvare il pittogramma.");
    } finally { setBusy(false); }
  };

  return <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
    <DialogContent showCloseButton={false} data-testid={`default-picto-modal-${picto.id}`}
      onCloseAutoFocus={event => { event.preventDefault(); onReturnFocus(); }}
      className="w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-sm bg-white p-5 sm:p-7">
      <DialogHeader className="text-left pr-7">
        <DialogTitle className="font-serif text-2xl">Personalizza pittogramma</DialogTitle>
        <DialogDescription className="sr-only">Nome, colore e categoria del pittogramma di default.</DialogDescription>
      </DialogHeader>
      <button type="button" onClick={onClose} disabled={busy} aria-label="Chiudi" title="Chiudi"
        className="absolute right-5 top-5 text-[#525252] hover:text-[#1A1A1A]" data-testid={`default-picto-modal-close-${picto.id}`}>
        <X className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-4 border-b border-[#E5E5E0] pb-4 min-w-0">
        <PictogramImage pictogram={{ ...picto, name, color_hex: effectiveColor }} className="w-16 h-16 shrink-0"
          testId={`default-picto-preview-${picto.id}`} />
        <span className="min-w-0 font-serif text-lg [overflow-wrap:anywhere]" data-testid={`default-picto-preview-name-${picto.id}`}>{name.trim() || picto.default_name}</span>
      </div>
      <div className="space-y-4 min-w-0">
        <div><label htmlFor={`picto-name-${picto.id}`} className="label-tiny">Nome visualizzato</label>
          <input id={`picto-name-${picto.id}`} className="input-luxury w-full" maxLength={80}
            value={name} onChange={e => setName(e.target.value)} placeholder={picto.default_name}
            data-testid={`default-picto-name-${picto.id}`} /></div>
        <div><label htmlFor={`picto-color-${picto.id}`} className="label-tiny">Colore</label>
          <div className="flex items-center gap-3">
            <input id={`picto-color-${picto.id}`} type="color" value={color}
              onChange={e => { setColor(e.target.value); setUseOriginal(false); }}
              className="h-10 w-14 border border-[#D4D4D4] cursor-pointer" data-testid={`default-picto-color-${picto.id}`} />
            <span className="text-xs text-[#525252] font-mono" data-testid={`default-picto-hex-${picto.id}`}>
              {useOriginal ? "Colore originale" : color.toUpperCase()}
            </span>
          </div>
          {picto.preserve_original && !picto.default_color_hex && <button type="button"
            className="text-xs underline text-[#525252] mt-2" onClick={() => setUseOriginal(!useOriginal)}
            data-testid={`default-picto-original-toggle-${picto.id}`}>
            {useOriginal ? "Applica colore selezionato" : "Usa colore originale"}
          </button>}</div>
        <div><label htmlFor={`picto-category-${picto.id}`} className="label-tiny">Categoria</label>
          <select id={`picto-category-${picto.id}`} className="input-luxury w-full"
            value={categoryId || ""} onChange={e => setCategoryId(e.target.value)}
            data-testid={`default-picto-category-${picto.id}`}>
            {current && !options.some(c => c.id === current.id) &&
              <option value={current.id} disabled>{current.name}</option>}
            {options.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
      </div>
      {error && <p role="alert" className="text-xs text-[#B91C1C]" data-testid={`default-picto-error-${picto.id}`}>{error}</p>}
      <div className="flex flex-wrap justify-end gap-2 border-t border-[#E5E5E0] pt-4">
        <button type="button" className="btn-ghost inline-flex items-center gap-2 text-xs"
          disabled={busy || (!picto.is_customized && !changed)} onClick={() => save(true)}
          data-testid={`default-picto-reset-${picto.id}`}><RotateCcw className="w-3.5 h-3.5" />Ripristina</button>
        <button type="button" className="btn-primary inline-flex items-center gap-2 text-xs"
          disabled={!changed || busy} onClick={() => save(false)}
          data-testid={`default-picto-save-${picto.id}`}><Save className="w-3.5 h-3.5" />Salva</button>
      </div>
    </DialogContent>
  </Dialog>;
};